import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { workspacePath } from '@wrapforce/shared/api-client';
import type { EmbedManageResponse, IntegrationSummary } from '@wrapforce/shared';
import type { QuoteService } from '@wrapforce/shared/constants';
import { api } from '@/lib/api';

interface EmbedWidgetSnippets {
  script: string;
  fallback: string;
}

type EmbedWidgetGetResponse = { widget: null } | { widget: EmbedManageResponse; snippets: EmbedWidgetSnippets };

interface EmbedWidgetCreateInput {
  shopName: string;
  destinationEmail: string;
  theme?: { accent?: string; mode?: 'light' | 'dark' | 'auto' };
  services: QuoteService[];
}

interface EmbedWidgetUpdateInput {
  shopName?: string;
  theme?: { accent?: string; mode?: 'light' | 'dark' | 'auto' };
  allowedDomains?: string[];
  status?: 'active' | 'paused';
  destinationEmail?: string;
  logoObjectKey?: string | null;
  services?: QuoteService[];
}

export function useIntegrations(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'integrations'],
    queryFn: () => api.get<{ items: IntegrationSummary[] }>(workspacePath(workspaceId!, '/integrations')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useConnectIntegration(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, body }: { provider: string; body?: unknown }) =>
      api.post<{ url: string } | { status: 'connected' }>(workspacePath(workspaceId!, `/integrations/${provider}/connect`), body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'integrations'] }),
  });
}

export function useDisconnectIntegration(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (provider: string) => api.post(workspacePath(workspaceId!, `/integrations/${provider}/disconnect`)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'integrations'] }),
  });
}

export function useStripeStatus(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'stripe-status'],
    queryFn: () => api.get<{ connected: boolean; chargesEnabled: boolean; accountIdMasked: string | null; payoutsEnabled: boolean; actionRequired: boolean }>(workspacePath(workspaceId!, '/stripe/status')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useConnectStripe(workspaceId: string | null) {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>(workspacePath(workspaceId!, '/stripe/connect')),
  });
}

export function useDisconnectStripe(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(workspacePath(workspaceId!, '/stripe/disconnect')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'stripe-status'] }),
  });
}

export function useEmbedWidget(workspaceId: string | null) {
  return useQuery({
    queryKey: [workspaceId, 'embed-widget'],
    queryFn: () => api.get<EmbedWidgetGetResponse>(workspacePath(workspaceId!, '/embed-widget')),
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useCreateEmbedWidget(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmbedWidgetCreateInput) =>
      api.post<{ widget: EmbedManageResponse; snippets: EmbedWidgetSnippets }>(workspacePath(workspaceId!, '/embed-widget'), body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'embed-widget'] }),
  });
}

export function useUpdateEmbedWidget(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EmbedWidgetUpdateInput) =>
      api.patch<{ widget: EmbedManageResponse }>(workspacePath(workspaceId!, '/embed-widget'), body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'embed-widget'] }),
  });
}

export function useEmbedLogoUpload(workspaceId: string | null) {
  return useMutation({
    mutationFn: (body: { contentType: 'image/jpeg' | 'image/png'; bytes: number }) =>
      api.post<{ uploadUrl: string; objectKey: string; expiresAt: string }>(workspacePath(workspaceId!, '/embed-widget/logo-upload'), body),
  });
}

export function useRegenerateEmbedKey(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ embedKey: string; snippets: EmbedWidgetSnippets }>(workspacePath(workspaceId!, '/embed-widget/regenerate-key')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'embed-widget'] }),
  });
}
